import { useEffect, useRef, useState } from 'react';
import { FileText, Gauge } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { formatDuration } from '@/lib/utils';
import { Button, Select } from '@/components/ui';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;
/** Cle de rappel de la vitesse choisie, commune a toutes les sessions. */
const SPEED_KEY = 'dgi.playbackRate';

interface VideoPlayerProps {
  src: string;
  captionsUrl?: string | null;
  transcript?: string | null;
  /** Position de reprise, en secondes. */
  startAt?: number;
  /** Appele lors des changements de position, pour enregistrer la reprise. */
  onPosition?: (seconds: number) => void;
}

/**
 * Lecteur video : reprise a la position quittee, vitesse memorisee,
 * sous-titres et transcription.
 * Video player: resume, remembered speed, captions and transcript.
 */
export function VideoPlayer({ src, captionsUrl, transcript, startAt = 0, onPosition }: VideoPlayerProps) {
  const { t, language } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [rate, setRate] = useState(1);
  const [showTranscript, setShowTranscript] = useState(false);
  const [resumed, setResumed] = useState(false);

  // La vitesse choisie vaut pour toutes les sessions : elle tient au confort
  // de l'agent, pas au contenu.
  useEffect(() => {
    try {
      const stored = Number(localStorage.getItem(SPEED_KEY));
      if ((SPEEDS as readonly number[]).includes(stored)) setRate(stored);
    } catch {
      /* stockage indisponible (navigation privee) */
    }
  }, []);

  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, [rate]);

  const applyRate = (value: number) => {
    setRate(value);
    try {
      localStorage.setItem(SPEED_KEY, String(value));
    } catch {
      /* stockage indisponible */
    }
  };

  /** La reprise n'a lieu qu'une fois, au chargement des metadonnees. */
  const onLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || resumed) return;
    video.playbackRate = rate;
    if (startAt > 0 && startAt < video.duration - 5) video.currentTime = startAt;
    setResumed(true);
  };

  return (
    <div className="space-y-3">
      <div className="aspect-video overflow-hidden rounded-xl bg-ink">
        <video
          ref={videoRef}
          src={src}
          controls
          preload="metadata"
          className="h-full w-full"
          onLoadedMetadata={onLoadedMetadata}
          onPause={() => onPosition?.(Math.floor(videoRef.current?.currentTime ?? 0))}
          onEnded={() => onPosition?.(0)}
        >
          {captionsUrl ? (
            <track kind="captions" src={captionsUrl} srcLang={language} label={t(`languages.${language}`)} default />
          ) : (
            <track kind="captions" />
          )}
        </video>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-ink-muted">
          <Gauge className="h-4 w-4" aria-hidden />
          {t('learn.playbackSpeed')}
          <Select
            value={String(rate)}
            onChange={(event) => applyRate(Number(event.target.value))}
            aria-label={t('learn.playbackSpeed')}
            className="h-9 w-24 py-1"
          >
            {SPEEDS.map((speed) => (
              <option key={speed} value={speed}>
                {speed}×
              </option>
            ))}
          </Select>
        </label>

        {startAt > 0 ? (
          <span className="text-xs text-ink-muted">
            {t('learn.resumeAt', { time: formatDuration(startAt, language) })}
          </span>
        ) : null}

        {transcript ? (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            icon={<FileText className="h-4 w-4" />}
            onClick={() => setShowTranscript((value) => !value)}
            aria-expanded={showTranscript}
          >
            {showTranscript ? t('learn.hideTranscript') : t('learn.showTranscript')}
          </Button>
        ) : null}
      </div>

      {transcript && showTranscript ? (
        <div className="max-h-80 overflow-y-auto rounded-xl bg-surface-muted p-4 text-sm leading-relaxed text-ink">
          {transcript.split('\n').map((paragraph, index) =>
            paragraph.trim() ? (
              <p key={index} className="mb-2">
                {paragraph}
              </p>
            ) : null,
          )}
        </div>
      ) : null}
    </div>
  );
}
